import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid, Select, MenuItem, FormControl, InputLabel,
  Chip, Stack, LinearProgress, Accordion, AccordionSummary, AccordionDetails, TextField, Tooltip,
  IconButton, Divider, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Tabs, Tab,
} from '@mui/material';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { WS_URL, TOKEN_KEY } from '../utils/constants';
import { backtestService, BacktestOptions, BacktestRunRow } from '../services/backtest.service';
import BacktestResults from '../components/backtest/BacktestResults';
import BacktestGuide from '../components/backtest/BacktestGuide';
import OptionInstanceBacktest from '../components/options/OptionInstanceBacktest';
import { BUILTIN_INTERVALS, CALENDAR_MONTH, intervalLabel } from '../utils/intervalCatalog';
import OptionSimTab from '../components/optionsim/OptionSimTab';

// 10m was missing here for no recorded reason — the chart has always offered
// it. 1M is excluded: a monthly backtest has too few bars to mean anything.
const TF_OPTIONS = BUILTIN_INTERVALS
  .filter(m => m !== CALENDAR_MONTH)
  .map(v => ({ v, l: intervalLabel(v) }));
const LIQUID_MAJORS = ['BTCUSD', 'ETHUSD', 'SOLUSD', 'BNBUSD', 'XRPUSD', 'DOGEUSD', 'ADAUSD', 'AVAXUSD', 'LINKUSD', 'LTCUSD', 'BCHUSD', 'DOTUSD', 'TRXUSD', 'XLMUSD', 'ETCUSD'];

/** Options-AT engines runnable from this page (same backend as the engine card's
 *  Backtest tab). Rudra is excluded — it has no backtest support yet (needs the
 *  deep option-history collection for month-long condor lifetimes). */
const OPTION_ENGINES: Array<{ id: string; label: string }> = [
  { id: 'thetaminer1', label: 'ThetaMiner 1 (near-ATM strangle)' },
  { id: 'thetaminer2', label: 'ThetaMiner 2 (ATM straddle)' },
  { id: 'deltaminercredit', label: 'DeltaMiner Credit (AV_Trend credit spread)' },
  { id: 'deltaminerdebit', label: 'DeltaMiner Debit (AV_Trend debit spread)' },
  { id: 'sudarsan', label: 'Sudarshan (clock strangle)' },
  { id: 'pinak', label: 'Pinak (clock strangle + recost)' },
];

/** The original Backtest & Optimization Studio (unchanged; the page's default tab). */
const BacktestStudio: React.FC = () => {
  const [options, setOptions] = useState<BacktestOptions | null>(null);
  const [runs, setRuns] = useState<BacktestRunRow[]>([]);
  const [selected, setSelected] = useState<BacktestRunRow | null>(null);
  const [loadingSel, setLoadingSel] = useState(false);
  const [live, setLive] = useState<{ id: number; pct: number; phase: string; detail?: string } | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // ── Setup form ──
  // 'rockband' = the native candle backtester below; the OPTION_ENGINES route to the
  // Options-AT multileg backtester (same component as the engine card's Backtest tab).
  const [engine, setEngine] = useState<string>('rockband');
  const [universe, setUniverse] = useState<'watchlist' | 'majors'>('majors');
  const [watchlistId, setWatchlistId] = useState<number | ''>('');
  const [tfs, setTfs] = useState<number[]>([240]);
  const [source, setSource] = useState<string>('binance');
  const [preset, setPreset] = useState<'quick' | 'standard' | 'thorough'>('standard');
  const [fee, setFee] = useState(0.05);
  const [slip, setSlip] = useState(5);
  const [amount, setAmount] = useState(10000);
  const [capital, setCapital] = useState(100000);
  const [split, setSplit] = useState(0.7);
  const [starting, setStarting] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const selectedWl = options?.watchlists?.find(w => w.id === watchlistId);
  // Watchlist management (create/delete/add/remove) lives on the Watchlist page —
  // this page only SELECTS a watchlist to backtest.

  const loadRuns = useCallback(async () => { try { setRuns(await backtestService.list()); } catch { /* */ } }, []);
  useEffect(() => {
    backtestService.getOptions().then(o => {
      setOptions(o);
      if (o.watchlists?.length) setWatchlistId(o.watchlists[0].id);
      if (o.defaults?.source) setSource(o.defaults.source);
    }).catch(() => toast.error('Failed to load backtest options'));
    loadRuns();
  }, [loadRuns]);

  // ── Live progress socket ──
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    const s = io(WS_URL, { auth: { token }, reconnection: true, forceNew: true });
    socketRef.current = s;
    s.on('backtest:progress', (d: any) => setLive(d));
    s.on('backtest:done', (d: any) => {
      setLive(null);
      loadRuns();
      setSelected(prev => {
        if (prev && prev.id === d.id) { openRun(d.id); }
        return prev;
      });
      if (d.status === 'DONE') toast.success('Backtest complete');
      else if (d.status === 'FAILED') toast.error(`Backtest failed: ${d.error || ''}`);
    });
    return () => { s.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRuns]);

  const openRun = useCallback(async (id: number) => {
    setLoadingSel(true);
    try { setSelected(await backtestService.get(id)); } catch { toast.error('Failed to load run'); } finally { setLoadingSel(false); }
  }, []);

  // Reliable polling fallback — drives progress + loads the result on completion
  // even if a live socket event is missed. Active only while a run is in flight.
  useEffect(() => {
    if (!selected || (selected.status !== 'RUNNING' && selected.status !== 'QUEUED')) return;
    const id = selected.id;
    const t = setInterval(async () => {
      try {
        const r = await backtestService.get(id);
        setSelected(cur => (cur && cur.id === id ? r : cur));
        if (r.status !== 'RUNNING' && r.status !== 'QUEUED') { setLive(null); loadRuns(); }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [selected?.id, selected?.status, loadRuns]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setStarting(true);
    try {
      const cfg: any = {
        strategyEngine: engine, source, timeframes: tfs, preset,
        feePctPerSide: fee, slippageBpsPerSide: slip, fixedAmount: amount, capital, trainTestSplit: split,
      };
      if (universe === 'watchlist') cfg.watchlistId = watchlistId; else cfg.symbols = LIQUID_MAJORS;
      const r = await backtestService.start(cfg);
      toast.success('Backtest started');
      await loadRuns();
      await openRun(r.id);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to start');
    } finally { setStarting(false); }
  };

  const cancel = async (id: number) => { try { await backtestService.cancel(id); toast('Cancelling…'); } catch { /* */ } };
  const remove = async (id: number) => { try { await backtestService.remove(id); if (selected?.id === id) setSelected(null); loadRuns(); } catch { /* */ } };

  // Native candle backtester engines (share the setup panel + results UI);
  // everything else routes to the option-instance backtester.
  const isNative = engine === 'rockband' || engine === 'bhairav' || engine === 'connors';
  const statusColor = (s: string) => s === 'DONE' ? 'success' : s === 'RUNNING' || s === 'QUEUED' ? 'info' : s === 'FAILED' ? 'error' : 'default';
  const liveForSelected = selected && live && live.id === selected.id ? live : null;
  const runningSelected = selected && (selected.status === 'RUNNING' || selected.status === 'QUEUED');

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ScienceIcon color="primary" />
        <Typography variant="h5" fontWeight={800}>Backtest &amp; Optimization Studio</Typography>
        <Chip size="small" label="No look-ahead · costs modelled · out-of-sample tested" variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<MenuBookIcon />} onClick={() => setGuideOpen(true)}>Guide &amp; Glossary</Button>
      </Stack>
      <BacktestGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

      <Grid container spacing={2}>
        {/* ── Left: setup + saved runs ── */}
        <Grid size={{ xs: 12, md: 4, lg: 3 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>New Backtest</Typography>

              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Strategy</InputLabel>
                <Select label="Strategy" value={engine} onChange={e => setEngine(e.target.value)}>
                  <MenuItem value="rockband">RockBand (OverView trend)</MenuItem>
                  <MenuItem value="bhairav">Bhairav (body-high/low S&R)</MenuItem>
                  <MenuItem value="connors">Connors RSI-2 (research — long pullbacks)</MenuItem>
                  {OPTION_ENGINES.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>

              {!isNative && (
                <Typography variant="caption" color="text.secondary">
                  Options auto-strategy — configure the range, symbol and lots on the right and hit <b>Run backtest</b>.
                  It replays the engine's exact live rules over collected Delta option MARK data.
                </Typography>
              )}

              {isNative && (<>
              <Typography variant="caption" fontWeight={700}>Universe</Typography>
              <ToggleButtonGroup size="small" exclusive fullWidth value={universe} onChange={(_, v) => v && setUniverse(v)} sx={{ mb: 1.5, mt: 0.5 }}>
                <ToggleButton value="majors">Liquid majors (15)</ToggleButton>
                <ToggleButton value="watchlist">Watchlist</ToggleButton>
              </ToggleButtonGroup>
              {universe === 'watchlist' && (
                <Box sx={{ mb: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Watchlist</InputLabel>
                    <Select label="Watchlist" value={watchlistId} onChange={e => setWatchlistId(e.target.value as number)}>
                      {(options?.watchlists || []).map(w => <MenuItem key={w.id} value={w.id}>{w.name} ({w.count})</MenuItem>)}
                      {!options?.watchlists?.length && <MenuItem value="" disabled>No watchlists yet</MenuItem>}
                    </Select>
                  </FormControl>
                  {!!watchlistId && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, maxHeight: 120, overflowY: 'auto' }}>
                      {(selectedWl?.symbols || []).map((s: any) => (
                        <Chip key={`${s.symbol}-${s.exchange}`} label={s.symbol} size="small"
                          color={(s.exchange || '').toUpperCase() === 'DELTA' ? 'default' : 'warning'}
                          variant="outlined" sx={{ height: 22 }} />
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Delta/Binance sources backtest the DELTA symbols; the Zerodha source backtests the
                    NSE/MCX/CDS ones. Manage watchlists on the <b>Watchlist</b> page.
                  </Typography>
                </Box>
              )}

              <Typography variant="caption" fontWeight={700}>Timeframes</Typography>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                {TF_OPTIONS.map(t => (
                  <Chip key={t.v} label={t.l} size="small" clickable color={tfs.includes(t.v) ? 'primary' : 'default'}
                    variant={tfs.includes(t.v) ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }}
                    onClick={() => setTfs(p => p.includes(t.v) ? p.filter(x => x !== t.v) : [...p, t.v].sort((a, b) => a - b))} />
                ))}
                <Tooltip title="4H/1D are the data-supported sweet spots for trend-riding; sub-4H tends to lose to costs (per the RockBand + Bhairav studies). 45m/90m/1W are aggregated when a source lacks them natively." arrow>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>4H / 1D recommended for trend strategies</Typography>
                </Tooltip>
              </Box>

              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>Data source</InputLabel>
                <Select label="Data source" value={source} onChange={e => setSource(e.target.value as any)}>
                  {(options?.sources || [{ value: 'delta', label: 'Delta' }, { value: 'binance', label: 'Binance' }]).map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>

              <Typography variant="caption" fontWeight={700}>Optimization depth</Typography>
              <ToggleButtonGroup size="small" exclusive fullWidth value={preset} onChange={(_, v) => v && setPreset(v)} sx={{ mb: 1.5, mt: 0.5 }}>
                <ToggleButton value="quick">Quick</ToggleButton>
                <ToggleButton value="standard">Standard</ToggleButton>
                <ToggleButton value="thorough">Thorough</ToggleButton>
              </ToggleButtonGroup>

              <Accordion disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="caption" fontWeight={700}>Backtest settings (costs, capital)</Typography></AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    <Tooltip title="Exchange/broker fee per side, % of notional (Delta taker ≈ 0.05%)." arrow><TextField size="small" type="number" label="Fee %/side" value={fee} onChange={e => setFee(+e.target.value)} inputProps={{ step: 0.01, min: 0 }} /></Tooltip>
                    <Tooltip title="Assumed slippage per side, in basis points (1 bp = 0.01%)." arrow><TextField size="small" type="number" label="Slippage bps/side" value={slip} onChange={e => setSlip(+e.target.value)} inputProps={{ step: 1, min: 0 }} /></Tooltip>
                    <Tooltip title="Fixed amount deployed per trade (your chosen sizing)." arrow><TextField size="small" type="number" label="Amount per trade (₹)" value={amount} onChange={e => setAmount(+e.target.value)} inputProps={{ step: 1000, min: 1 }} /></Tooltip>
                    <Tooltip title="Starting capital for the equity-curve baseline." arrow><TextField size="small" type="number" label="Starting capital (₹)" value={capital} onChange={e => setCapital(+e.target.value)} inputProps={{ step: 10000, min: 1 }} /></Tooltip>
                    <Tooltip title="Fraction used for in-sample optimisation; the rest is held out (out-of-sample) to flag overfitting." arrow><TextField size="small" type="number" label="Train/test split" value={split} onChange={e => setSplit(+e.target.value)} inputProps={{ step: 0.05, min: 0.3, max: 0.9 }} /></Tooltip>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Button fullWidth variant="contained" startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                disabled={starting || !tfs.length || (universe === 'watchlist' && !watchlistId)} onClick={start}>
                Run Backtest
              </Button>
              </>)}
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ pb: '8px !important' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" fontWeight={800}>Saved runs</Typography>
                <IconButton size="small" onClick={loadRuns}><RefreshIcon fontSize="small" /></IconButton>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={0.5} sx={{ maxHeight: 460, overflowY: 'auto' }}>
                {runs.map(r => {
                  const liveR = live && live.id === r.id ? live : null;
                  const showPct = liveR ? liveR.pct : r.progressPct;
                  return (
                    <Box key={r.id} onClick={() => openRun(r.id)} sx={{ p: 1, borderRadius: 1, cursor: 'pointer', border: '1px solid', borderColor: selected?.id === r.id ? 'primary.main' : 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: 150 }}>{r.label || `Run ${r.id}`}</Typography>
                        <Chip size="small" label={r.status} color={statusColor(r.status) as any} sx={{ height: 18, fontSize: '0.6rem' }} />
                      </Stack>
                      {(r.status === 'RUNNING' || r.status === 'QUEUED') && <LinearProgress variant="determinate" value={showPct} sx={{ mt: 0.5, height: 4, borderRadius: 1 }} />}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">{new Date(r.createdAt).toLocaleDateString('en-IN')}</Typography>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); remove(r.id); }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
                      </Stack>
                    </Box>
                  );
                })}
                {!runs.length && <Typography variant="caption" color="text.secondary">No runs yet — configure and Run Backtest.</Typography>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Right: progress / results ── */}
        <Grid size={{ xs: 12, md: 8, lg: 9 }}>
          {!isNative && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                  {OPTION_ENGINES.find(o => o.id === engine)?.label} — options backtest
                </Typography>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Replays this engine's exact live rules over collected Delta option MARK candles (BTC/ETH) with the
                  engine's default settings. To backtest a customised configuration, use the same Backtest tab on the
                  engine's card in <b>Strategies → Options AT</b> (Settings · Rules · Backtest).
                </Alert>
                <OptionInstanceBacktest engine={engine} intervalMinutes={15} symbols={['BTCUSD', 'ETHUSD']} />
              </CardContent>
            </Card>
          )}
          {isNative && !selected && <Card><CardContent><Alert severity="info">Configure a backtest on the left and click <b>Run Backtest</b>. Results — equity curve, drawdown, per-symbol, ranked combinations and a plain-language "why #1" — appear here. Best practices (no look-ahead, realistic costs, out-of-sample testing) are applied automatically.</Alert></CardContent></Card>}

          {isNative && selected && runningSelected && (
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" fontWeight={700}>{selected.label}</Typography>
                  <Button size="small" color="error" startIcon={<StopIcon />} onClick={() => cancel(selected.id)}>Cancel</Button>
                </Stack>
                <LinearProgress variant="determinate" value={liveForSelected?.pct ?? selected.progressPct} sx={{ height: 10, borderRadius: 1, mb: 1 }} />
                <Typography variant="body2">{liveForSelected?.phase || selected.phase || 'Queued…'} — {liveForSelected?.pct ?? selected.progressPct}%</Typography>
                <Typography variant="caption" color="text.secondary">{liveForSelected?.detail || selected.progressDetail || ''}</Typography>
              </CardContent>
            </Card>
          )}

          {isNative && selected && selected.status === 'FAILED' && <Card><CardContent><Alert severity="error">Backtest failed: {selected.error}</Alert></CardContent></Card>}
          {isNative && selected && selected.status === 'CANCELLED' && <Card><CardContent><Alert severity="warning">This run was cancelled.</Alert></CardContent></Card>}
          {isNative && loadingSel && <LinearProgress sx={{ mt: 1 }} />}
          {isNative && selected && selected.status === 'DONE' && selected.result && <BacktestResults result={selected.result} />}
        </Grid>
      </Grid>
    </Box>
  );
};

type PageTab = 'studio' | 'optionsim';

/**
 * Backtest page: the Studio (default) and the BTC Options Simulator (research/options-sim,
 * owner answer 5). The tab is in the URL (?tab=optionsim) so a reload or a link lands on it.
 * Each tab mounts on first visit and then stays mounted (hidden), so switching keeps its state.
 */
const BacktestPage: React.FC = () => {
  const [tab, setTab] = useState<PageTab>(() => (new URLSearchParams(window.location.search).get('tab') === 'optionsim' ? 'optionsim' : 'studio'));
  const [seen, setSeen] = useState<Record<PageTab, boolean>>(() => ({ studio: tab === 'studio', optionsim: tab === 'optionsim' }));
  const select = useCallback((v: PageTab) => {
    setTab(v);
    setSeen(p => (p[v] ? p : { ...p, [v]: true }));
    const url = new URL(window.location.href);
    if (v === 'studio') url.searchParams.delete('tab'); else url.searchParams.set('tab', v);
    window.history.replaceState(null, '', url.toString());
  }, []);
  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => select(v)} variant="scrollable" allowScrollButtonsMobile
        sx={{ mb: 2, minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 700 } }}>
        <Tab value="studio" icon={<ScienceIcon fontSize="small" />} iconPosition="start" label="Backtest Studio" />
        <Tab value="optionsim" icon={<CandlestickChartIcon fontSize="small" />} iconPosition="start" label="Options Simulator" data-testid="tab-optionsim" />
      </Tabs>
      {seen.studio && <Box sx={{ display: tab === 'studio' ? 'block' : 'none' }}><BacktestStudio /></Box>}
      {seen.optionsim && <Box sx={{ display: tab === 'optionsim' ? 'block' : 'none' }}><OptionSimTab /></Box>}
    </Box>
  );
};

export default BacktestPage;
